import React, { useEffect, useState, useRef } from 'react'
import ApiService from '../../ApiService'
import DatePicker from 'react-datepicker'
import moment from 'moment'
import CountDown from './Countdown'
import CircularProgress from '@material-ui/core/CircularProgress'
import removeCSS from '../../util/RemoveCSS'
import './style.css'
import useUserStore from '../../hook/useUserStore'
import { DateTime } from 'luxon'
import { parseAxiosError } from '../../util/parseHelper'
import Webcam from 'react-webcam'
import { isValidValue } from '../../util/common'
import { useZxing } from 'react-zxing'
import { MultiFormatReader, BarcodeFormat, DecodeHintType } from '@zxing/library'
import CameraModal from './CameraModal'
import * as Sentry from '@sentry/react'

const isLocal = ['development', 'sandbox'].includes((process.env.REACT_APP_ENV || '').toLowerCase())

const normalizeKitId = kitId => {
  if (!kitId) return ''

  if (isLocal) {
    return kitId.trim()
  }
  return kitId.trim().toUpperCase()
}

const BINARY_GENDERS = ['male', 'female']
const LAB_GENDER_ERROR_MESSAGE = 'Please select Male or Female to meet lab requirements.'
const normalizeGenderAtBirth = gender => (typeof gender === 'string' ? gender.trim().toLowerCase() : '')

// Constants
const MIN_KIT_ID_LENGTH = 5
const ErrorMsg = {
  Common: 'We’re having trouble registering this kit. Please check the kit ID and try again or contact customer care.',
  NotFound: `Sorry, the Kit ID you have entered is not valid. Please re-enter to try again`,
}
const AutoRegisteredMessage =
  'THIS KIT HAS BEEN AUTOREGISTERED. If you are part of our PrEP program, you are all set -- please just send your kit back.'

// Kit IDs that belong to a partner program and must register on the partner's site instead.
const PARTNER_REDIRECT_KIT_IDS = new Set(['TBD94500360', 'TBD94490065', 'TBD94500376'])
const PARTNER_REDIRECT_URL = 'https://www.tbd.health/mshs-wisp/registerkit'
// Whitespace-insensitive (strips embedded scanner noise too) and case-insensitive.
const matchesPartnerRedirectKit = kitId => PARTNER_REDIRECT_KIT_IDS.has((kitId || '').replace(/\s/g, '').toUpperCase())

// ────────────────────────────────────────────────────────────────
// HelpModal Component (unchanged from your base code)
// ────────────────────────────────────────────────────────────────
const HelpModal = ({ hide, setHelpSent }) => {
  const [reason, setReason] = useState('')
  const [localImg, setLocalImg] = useState('')
  const [formPreparedData, setFormPreparedData] = useState()
  const webcamRef = useRef(null)
  const [hasCamera, setHasCamera] = useState(false)
  const [error, setError] = useState('')
  const [isRemoteCallInProgress, setIsRemoteCallInProgress] = useState(false)

  useEffect(() => {
    setError(null)
    navigator.mediaDevices
      .enumerateDevices()
      .then(devices => {
        const cameras = devices.filter(device => device.kind === 'videoinput')
        setHasCamera(cameras.length > 0)
      })
      .catch(error => {
        console.error('Error checking camera availability:', error)
        setHasCamera(false)
      })
  }, [])

  const capture = async () => {
    setError(null)
    if (!webcamRef.current) return
    const imageSrc = webcamRef.current.getScreenshot()
    if (!imageSrc || imageSrc.length === 0) {
      setFormPreparedData(null)
      return
    }
    // Convert base64 image data to Blob
    const byteCharacters = atob(imageSrc.split(',')[1])
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    const blob = new Blob([byteArray], { type: 'image/jpeg' })

    let questionTxt = isValidValue(reason) ? reason : document.getElementById('reason').value
    setReason(questionTxt)
    const formData = new FormData()
    formData.append('file', blob, 'kit.jpg')
    formData.append('questionType', 'REGISTER_KIT')
    formData.append('questionText', questionTxt)
    setFormPreparedData(formData)
    setLocalImg(imageSrc)
  }

  const onClick = async () => {
    setError(null)
    setHelpSent(false)
    let questionTxt = isValidValue(reason) ? reason : document.getElementById('reason').value
    setReason(questionTxt)
    if (!isValidValue(questionTxt)) {
      setError('Please input your question.')
      return
    }
    try {
      setIsRemoteCallInProgress(true)
      if (hasCamera && formPreparedData) {
        formPreparedData.delete('questionText')
        formPreparedData.append('questionText', questionTxt)
        await ApiService.postHelp(formPreparedData)
        setHelpSent(true)
      } else {
        const formData = new FormData()
        formData.append('questionType', 'REGISTER_KIT')
        formData.append('questionText', questionTxt || 'EMPTY')
        await ApiService.postHelp(formData)
        setHelpSent(true)
      }
    } catch (error) {
      setError('Failed to send help request. Please try again.')
    } finally {
      hide()
      setIsRemoteCallInProgress(false)
    }
  }

  return (
    <div className="helpmodal">
      <div className="helpmodal-body">
        <div className="helpmodal-close" onClick={hide}>
          x
        </div>
        <div className="helpmodal-title">GET HELP</div>
        <div className="helpmodal-desc">Please enter below any issues you're having with registering kit and our team will help you</div>
        <br />
        {error && <div className="registerKit-errors">{error}</div>}
        <br />
        <input
          type="text"
          id="reason"
          className="login_inputs placeholderhelp"
          placeholder="type your issue here..."
          value={reason}
          onChange={e => setReason(e.target.value)}
          onInput={e => setReason(e.target.value)}
        />
        <br />
        {!localImg && hasCamera && <div className="helpmodal-desc">Please upload a photo of the KIT you are registering</div>}
        {localImg && <img alt="capture" src={localImg} className="helpmodal-preview" />}
        {!localImg && hasCamera && (
          <div className="helpmodal-camera">
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              className="helpmodal-webcam"
              videoConstraints={{
                width: 1280,
                height: 720,
                facingMode: { exact: 'environment' },
              }}
              forceScreenshotSourceSize={true}
            />
          </div>
        )}
        {!localImg && hasCamera && (
          <div className="row registerKit-login-buttons">
            <button type="button" onClick={capture} className="btn socialbutton-customizable" id="socialbutton-customizable">
              Capture
            </button>
          </div>
        )}
        {localImg && (
          <div className="row registerKit-login-buttons">
            <button disabled={!reason || isRemoteCallInProgress} type="button" onClick={onClick} className="btn socialbutton-customizable" id="socialbutton-customizable">
              Submit
            </button>
          </div>
        )}
        {!hasCamera && (
          <div className="row registerKit-login-buttons">
            <button disabled={!reason || isRemoteCallInProgress} type="button" onClick={onClick} className="btn socialbutton-customizable" id="socialbutton-customizable">
              Submit
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default function RegisterKit() {
  const [kitNumber, setKitNumber] = useState('')
  const [testTakenDate, setTestTakenDate] = useState(false)
  const [symptoms, setSymptoms] = useState('')
  const [medications, setMedications] = useState('')
  const [page, setPage] = useState(1)
  const [error, setError] = useState('')
  const [dob, setDob] = useState(null)
  const [helpModal, setHelpModal] = useState(false)
  const [helpSent, setHelpSent] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const [scanTipVisible, setScanTipVisible] = useState(false)
  const [showSampleBarcode, setShowSampleBarcode] = useState(false)
  const [labSpecificGenderAtBirth, setLabSpecificGenderAtBirth] = useState('')

  const patient = useUserStore(state => state.patient)
  const normalizedPatientGenderAtBirth = normalizeGenderAtBirth(patient?.genderAtBirth)
  const patientLabSpecificGenderAtBirth =
    typeof patient?.labSpecificGenderAtBirth === 'string' ? patient.labSpecificGenderAtBirth.trim() : ''
  const hasPatientLabSpecificGender = !!patientLabSpecificGenderAtBirth
  const shouldSendLabGender =
    !!normalizedPatientGenderAtBirth && !BINARY_GENDERS.includes(normalizedPatientGenderAtBirth)
  const shouldCollectLabGender = shouldSendLabGender && !hasPatientLabSpecificGender
  const labGenderForPayload = hasPatientLabSpecificGender ? patientLabSpecificGenderAtBirth : labSpecificGenderAtBirth

  useEffect(() => {
    removeCSS()
    document.body.classList.add('body_portal')
    if (patient.dob && patient.dob.trim() !== '') {
      setDob(DateTime.fromISO(patient.dob).toJSDate())
    }
  }, [patient?.dob])

  useEffect(() => {
    if (hasPatientLabSpecificGender) {
      setLabSpecificGenderAtBirth(patientLabSpecificGenderAtBirth)
      return
    }
    if (!shouldSendLabGender) {
      setLabSpecificGenderAtBirth('')
    }
  }, [hasPatientLabSpecificGender, patientLabSpecificGenderAtBirth, shouldSendLabGender])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!kitNumber) setScanTipVisible(true)
    }, 5000)
    return () => clearTimeout(timer)
  }, [kitNumber])

  // Partner-program kits must register on the partner's site. Redirect as soon
  // as a matching ID is typed, pasted, or scanned into the kit number field.
  useEffect(() => {
    if (matchesPartnerRedirectKit(kitNumber)) {
      window.location.href = PARTNER_REDIRECT_URL
    }
  }, [kitNumber])

  const handleLabGenderChange = event => {
    setLabSpecificGenderAtBirth(event.target.value)
    if (error === LAB_GENDER_ERROR_MESSAGE) {
      setError('')
    }
  }

  const handleSubmit = event => {
    // Defense-in-depth: a partner kit must never reach POST /secure/kit, even if it
    // was entered just before the redirect effect ran. Redirect and skip submission.
    if (matchesPartnerRedirectKit(kitNumber)) {
      window.location.href = PARTNER_REDIRECT_URL
      return
    }
    setError('')
    if (kitNumber.length < MIN_KIT_ID_LENGTH) {
      setError(ErrorMsg.Common)
      return
    }
    if (!testTakenDate) {
      setError('Make sure to choose the date you took the sample.')
      return
    }
    if (!dob) {
      setError('Please enter and confirm birth date')
      return
    }
    const dobDT = DateTime.fromJSDate(dob).startOf('day')
    if (!dobDT.isValid) {
      setError('Invalid birth date. Please re-enter your date of birth.')
      return
    }
    const today = DateTime.now().startOf('day')
    if (dobDT > today) {
      setError('Birth date cannot be in the future. Please re-enter your date of birth.')
      return
    }
    if (today < dobDT.plus({ years: 18 })) {
      setError('You must be at least 18 years old to register a kit. Please verify your birth date or reach out to customer care for assistance.')
      return
    }
    if (shouldSendLabGender && !labGenderForPayload) {
      setError(LAB_GENDER_ERROR_MESSAGE)
      return
    }
    setPage(3)
    const payload = {
      kitId: normalizeKitId(kitNumber),
      testTakenDate: testTakenDate,
      dob: dob ? DateTime.fromJSDate(dob).toISODate() : '',
      symptoms: symptoms,
      medications: medications,
    }
    if (shouldSendLabGender && labGenderForPayload) {
      payload.labSpecificGenderAtBirth = labGenderForPayload
    }
    async function registerKitCaller() {
      try {
        await ApiService.postRegisterKit(payload)
        setKitNumber('')
        setLabSpecificGenderAtBirth('')
        setPage(2)
      } catch (err) {
        const parsedError = parseAxiosError(err)
        // Display a specific message for autoregistered kits instead of generic errors
        if (
          parsedError?.message?.includes('already autoregistered') ||
          parsedError?.message?.includes('already registered')
        ) {
          setError(AutoRegisteredMessage)
          setPage(1)
          return
        }
        if (parsedError?.code === 422) {
          setError(parsedError.message)
          setPage(1)
          return
        }
        const sentryError = new Error('Kit registration failed')
        Sentry.captureException(sentryError, {
          extra: {
            originalError: err?.message,
            parsedMessage: parsedError?.message,
            userId: patient?.userId || 'unknown',
            payload: {
              kitId: normalizeKitId(kitNumber),
              userId: patient?.userId || 'unknown',
            },
          },
          tags: {
            feature: 'register-kit',
          },
          contexts: {
            registerKitPayload: {
              kitId: normalizeKitId(kitNumber),
              userId: patient?.userId || 'unknown',
            },
          },
        })

        setError(parsedError?.message ?? 'Request failed. Please try again')
        setPage(1)
      }
    }
    registerKitCaller()
  }

  const handleBarcodeScan = scannedValue => {
    setKitNumber(scannedValue)
    setScanTipVisible(false)
  }

  return (
    <div className="form" id="registerKit-form">
      <div className="form-body registerkit-body">
        <div className="panel-form" id="panel">
          <form id="kitRegistrationForm" className="needs-validation" noValidate>
            <div className="registerKit-errors">{error}</div>
            {error &&
              error !== AutoRegisteredMessage && (
                <div className="registerKit-errors">
                  Also make sure that the Kit ID exactly matches your kit's registration card
                </div>
              )}
            <div className="row w-100-mobile kitRegistrationForm">
              <div className="form-group register-kit-form">
                {page === 1 ? (
                  <>
                    <span className="registerkit_text mb">Register Your TBD Kit</span>

                    <span htmlFor="kitNumber" className="registerKit-label">
                      Open your phone's camera to scan the barcode located on your kit's Registration Card or enter the Kit ID manually
                    </span>
                    <div style={{ width: '100%' }}>
                      <CameraModal
                        onScan={handleBarcodeScan}
                        inputComponent={
                          <input
                            type="text"
                            id="kitNumberRegister"
                            className={error === ErrorMsg.Common ? 'registerKitFieldsNew red-outline' : 'registerKitFieldsNew'}
                            required
                            placeholder="Enter Kit number"
                            value={kitNumber}
                            onInput={e => setKitNumber(e.target.value)}
                          />
                        }
                      />
                    </div>
                    {showSampleBarcode && (
                      <div
                        style={{
                          position: 'fixed',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          backgroundColor: 'rgba(0, 0, 0, 0.85)',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          zIndex: 1000,
                          padding: '20px',
                        }}
                      >
                        <div style={{ position: 'relative', backgroundColor: '#fefaf0', borderRadius: '8px', overflow: 'hidden' }}>
                          <div
                            style={{
                              width: '100%',
                              fontSize: '16px',
                              color: 'black',
                              padding: '10px',
                              textAlign: 'left',
                              paddingRight: '60px',
                            }}
                          >
                            Your barcode is located on the registration card as shown below
                          </div>
                          <img
                            alt="barcode-sample"
                            src="/img/barcode-sample.jpg"
                            style={{
                              maxWidth: '85vw',
                              maxHeight: '90vh',
                              borderRadius: '8px',
                              margin: '8px',
                              // border not rounded visible
                            }}
                          />
                          <button
                            onClick={() => setShowSampleBarcode(false)}
                            style={{
                              position: 'absolute',
                              top: '0px',
                              right: '5px',
                              background: 'none',
                              border: 'none',
                              fontSize: '36px',
                              cursor: 'pointer',
                              color: 'black',
                            }}
                          >
                            &times;
                          </button>
                        </div>
                      </div>
                    )}
                    <div
                      style={{
                        width: '100%',
                        fontSize: '14px',
                        textAlign: 'left',
                        color: '#1F7F61',
                        marginTop: '-20px',
                        marginBottom: '20px',
                      }}
                    >
                      Helpful tip: if you are entering your Kit ID manually, make sure to include any prefixes and dashes. Ensure there are no spaces before or after.
                    </div>
                    <div
                      style={{
                        width: '100%',
                        cursor: 'pointer',
                        fontSize: '14px',
                        textAlign: 'left',
                        marginBottom: '10px',
                      }}
                      onClick={() => setShowSampleBarcode(true)}
                    >
                      Facing issues finding your Barcode?{' '}
                      <span
                        style={{
                          textDecoration: 'underline',
                          color: '#1F7F61',
                        }}
                      >
                        Click here
                      </span>
                    </div>
                    <span htmlFor="dob" className="registerKit-label">
                      Day sample was taken?
                    </span>
                    <DatePicker
                      className={error.includes('sample') ? 'registerKitFieldsNew red-outline' : 'registerKitFieldsNew'}
                      placeholderText="Select sample date"
                      name="testTakenDate"
                      id="testTakenDate"
                      maxDate={new Date()}
                      selected={testTakenDate ? moment(testTakenDate, 'YYYY-MM-DD').toDate() : false}
                      onChange={date => setTestTakenDate(date)}
                    />

                    <span className="registerKit-label">Any symptoms?</span>
                    <textarea
                      className="registerKitFieldsNew registerKitFieldsNewTextArea"
                      placeholder="Describe any symptoms..."
                      value={symptoms}
                      onInput={e => setSymptoms(e.target.value)}
                    />

                    <span className="registerKit-label">Are you taking any medications?</span>
                    <textarea
                      className="registerKitFieldsNew registerKitFieldsNewTextArea"
                      placeholder="Mention any medications..."
                      value={medications}
                      onInput={e => setMedications(e.target.value)}
                    />

                    <span className="registerKit-label">Please confirm your birthdate</span>
                    <DatePicker
                      className={error.includes('birth') ? 'registerKitFieldsNew red-outline' : 'registerKitFieldsNew'}
                      placeholderText="Select your birthdate"
                      name="dob"
                      selected={dob ? dob : null}
                      onChange={date => setDob(date)}
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      maxDate={new Date()}
                    />

                    {shouldCollectLabGender && (
                      <div className="lab-sex-selection">
                        <p className="registerKit-label" style={{ fontWeight: 'normal', marginBottom: '10px' }}>
                          To meet laboratory requirements and ensure you receive the most appropriate clinical care, we need to record your sex assigned at birth (as listed on your original birth certificate).
                        </p>
                        <p className="registerKit-label" style={{ fontWeight: 'normal', marginBottom: '10px' }}>
                          We understand that not everyone may feel these options reflect their body or identity, and that biological variations such as intersex exist. However, because our lab currently only accepts 'male' or 'female,' these are the options we can provide.
                        </p>
                        <p className="registerKit-label" style={{ fontWeight: 'normal', marginBottom: '10px' }}>
                          If you would like to share your current gender identity or pronouns with your care team, you are welcome to do so separately. We value all aspects of your identity, and your information is confidential and used only to improve your care experience.
                        </p>
                        <label htmlFor="labSpecificGenderAtBirth" className="registerKit-label">
                          Please select one:
                        </label>
                        <select
                          id="labSpecificGenderAtBirth"
                          className={error === LAB_GENDER_ERROR_MESSAGE ? 'registerKitFieldsNew red-outline' : 'registerKitFieldsNew'}
                          value={labSpecificGenderAtBirth}
                          onChange={handleLabGenderChange}
                        >
                          <option value="">Select</option>
                          <option value="MALE">Male</option>
                          <option value="FEMALE">Female</option>
                        </select>
                      </div>
                    )}

                    <div style={{ marginTop: '-40px', marginBottom: '20px' }}>
                      <input type="checkbox" id="confirmation" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
                      <label htmlFor="confirmation" className="registerKit-label" style={{ fontWeight: 'normal' }}>
                        I confirm I submitted this sample. TBD Health releases results only to the person who submitted the kit.
                      </label>
                    </div>

                    <div id="submitButtonDiv" className="row registerKit-login-buttons">
                      <button type="button" onClick={handleSubmit} className="btn socialbutton-customizable1" id="socialbutton-customizable1" disabled={!confirmed}>
                        Register Kit
                      </button>
                    </div>
                  </>
                ) : page === 2 ? (
                  <CountDown className="count-down" />
                ) : (
                  <>
                    <div className="util_text">Please wait while we process your request...</div>
                    <div className="flex just-cent">
                      <CircularProgress className="loading-button-spinner" />
                    </div>
                  </>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
